<template>
  <Layout>
    <div class="page-header">
      <div class="page-title">
        <h2>{{$t('agents_title')}}</h2>
        <h5>{{$t('agents_description')}}</h5>
      </div>

      <div class="actions">
        <button @click="openCreateModal" class="justify-end-add-btn no-text-select">
          <img src="../assets/images/plus.svg" />
          <span>{{ $t('create_agent') }}</span>
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>{{$t('loading')}}</p>
    </div>

    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <button @click="fetchAgents" class="retry-button">{{ $t('retry') }}</button>
    </div>

    <div v-else-if="agents.length > 0" class="agents-list-grid">
      <AgentCard
          v-for="agent in agents"
          :key="agent.id"
          :agent="agent"
          :onClick="navigateToAgent"
      />
    </div>

    <div v-else class="empty-state">
      <h3>{{$t('no_agents_found')}}</h3>
      <p>{{ $t("create_your_first_agent_to_get_started") }}</p>
      <button @click="openCreateModal" class="action-button">
        <img src="../assets/images/plus.svg" />
        {{ $t('create_agent') }}
      </button>
    </div>

    <div v-if="pagination.totalItems > 0" class="pagination-align-center">
      <Pagination
          :current-page="pagination.currentPage"
          :total-items="pagination.totalItems"
          :per-page="pagination.itemsPerPage"
          :on-page-change="handlePage"
      />
    </div>


    <CreateAgentModal
        :visible="createAgentModalVisible"
        :onHide="() => { createAgentModalVisible = false; fetchAgents(currentPage); }"
        :on-action="handleCreateAgent"
    />
  </Layout>
</template>

<script>
import Layout from "@/components/layouts/Layout.vue";
import TestFilters from "@/components/tables/Test/TestFilters.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import Pagination from "@/components/Pagination.vue";
import AgentCard from "@/components/AgentsCard.vue";
import agentService from "@/service/agentService";
import CreateAgentModal from "@/components/modals/CreateAgentModal.vue";
import {handleErrorResponse} from "@/utils/utils";

export default {
  name: 'Agents',
  components: {
    CreateAgentModal,
    AgentCard,
    Pagination,
    ExpandableSearch,
    TestFilters,
    Layout
  },
  data() {
    return {
      isLoading: false,
      error: null,
      currentPage: 1,
      agents: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10
      },
      createAgentModalVisible: false
    };
  },
  mounted() {
    this.fetchAgents(1);
  },
  methods: {
    async fetchAgents(page = 1) {
      this.isLoading = true;
      this.error = null;

      try {
        const companyId = JSON.parse(localStorage.getItem('user'))?.companyId;

        const response = await agentService.getAgents(companyId, page, this.pagination.itemsPerPage);

        if (response.data.success) {
          this.agents = response.data.agents;
          this.pagination = response.data.pagination;
          this.currentPage = page;
        } else {
          throw new Error('Failed to fetch agents');
        }
      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to load agents. Please try again.';
        this.agents = [];
      } finally {
        this.isLoading = false;
      }
    },
    handlePage(page) {
      this.fetchAgents(page);
    },
    async handleCreateAgent(data) {
      const response = await agentService.createAgent(data);
      if (response.data.success) {
        this.fetchAgents(this.currentPage);
      } else {
        throw new Error(this.$t('failed_to_create_agent'));
      }
    },
    navigateToAgent(agentId) {
      this.$router.push(`/agent/${agentId}`);
    },
    openCreateModal() {
      this.createAgentModalVisible = true;
    }
  }
}
</script>

<style scoped>
.agents-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: #667085;
  font-family: euclid_regular, sans-serif;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.error-message {
  color: #D92D20;
  font-family: euclid_regular, sans-serif;
  margin-bottom: 16px;
  text-align: center;
}

.retry-button {
  background-color: #667085;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  transition: background-color 0.2s ease;
}

.retry-button:hover {
  background-color: #475467;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state h3 {
  font-size: 20px;
  color: #040C2D;
  margin-bottom: 8px;
  font-family: euclid_medium, sans-serif;
}

.empty-state p {
  font-size: 16px;
  color: #667085;
  font-family: euclid_regular, sans-serif;
  margin-bottom: 24px;
}

.create-first-agent-btn {
  background-color: #667085;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-family: euclid_medium, sans-serif;
  transition: background-color 0.2s ease;
}

.create-first-agent-btn:hover {
  background-color: #475467;
}

.pagination-align-center {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

@media (max-width: 768px) {
  .agents-list-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
